import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { apiRequest, downloadFile } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const EVENTS = [["", "Tutti gli eventi"], ["login_ok", "Accessi"], ["login_fail", "Accessi falliti"], ["login_blocked", "Accessi bloccati"],
  ["otp_fail", "Codice 2FA errato"], ["uso", "Uso dell'app"], ["register", "Nuovi profili"], ["invite_accept", "Inviti accettati"]];
const BADGE = { login_ok: "bg-emerald-100 text-emerald-800", login_fail: "bg-rose-100 text-rose-800", login_blocked: "bg-rose-100 text-rose-800",
  otp_fail: "bg-rose-100 text-rose-800", uso: "bg-slate-100 text-slate-700", register: "bg-blue-100 text-blue-800", invite_accept: "bg-blue-100 text-blue-800" };
const ALERT_STYLE = { tentativi: "border-rose-300 bg-rose-50", bloccato: "border-rose-300 bg-rose-50", ip_nuovo: "border-amber-300 bg-amber-50", molti_ip: "border-amber-300 bg-amber-50" };
const EMPTY = { event: "", who: "", ip: "", since: "", until: "", new_ip: false };
const PAGE = 100;

const when = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" }); } catch { return iso; } };
const qs = (f, extra = {}) => new URLSearchParams(Object.entries({ ...f, ...extra }).filter(([, v]) => v !== "" && v !== false && v != null).map(([k, v]) => [k, String(v)])).toString();

// Registro degli accessi (solo SUPER_ADMIN): chi entra, quando, da quale indirizzo IP.
export default function AccessLog() {
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (f, offset = 0) => {
    setBusy(true);
    try {
      const r = await apiRequest(`/admin/access-log?${qs(f, { limit: PAGE, offset })}`);
      setTotal(r.total);
      setRows((cur) => (offset ? [...cur, ...r.rows] : r.rows));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, []);
  const loadSummary = useCallback(() => apiRequest("/admin/access-log/summary").then(setSummary).catch((e) => toast.error(e.message)), []);

  useEffect(() => { load(EMPTY); loadSummary(); }, [load, loadSummary]);

  const apply = (f) => { setFilters(f); setApplied(f); load(f); };
  const csv = () => downloadFile(`/admin/access-log/export.csv?${qs(applied)}`, "registro-accessi.csv").catch((e) => toast.error(e.message));
  const ev = (k) => (summary ? summary.last_24h.events[k] || 0 : "-");

  return (
    <div className="space-y-4" data-testid="access-log-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Registro accessi</h1>
          <p className="text-sm text-slate-600">Chi entra nell'app, quando e da quale indirizzo IP. Si conserva per 400 giorni. Non contiene mai password né codici.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { load(applied); loadSummary(); }}><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
          <Button variant="outline" size="sm" onClick={csv}><Download className="h-4 w-4" /> Scarica CSV</Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["Accessi, ultime 24 ore", ev("login_ok")], ["Accessi falliti o bloccati", summary ? ev("login_fail") + ev("login_blocked") + ev("otp_fail") : "-"],
          ["Indirizzi IP diversi", summary ? summary.last_24h.ips : "-"], ["Persone attive", summary ? summary.last_24h.people : "-"]].map(([l, v]) => (
          <Card key={l}><CardContent className="pt-4"><p className="text-3xl font-bold text-slate-900">{v}</p><p className="text-sm text-slate-600">{l}</p></CardContent></Card>))}
      </div>

      {summary && summary.alerts.length > 0 && (
        <Card className="border-amber-300"><CardHeader><CardTitle className="flex items-center gap-2 text-base"><ShieldAlert className="h-5 w-5 text-amber-700" /> Da guardare</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {summary.alerts.map((a, i) => (
              <button key={i} className={`flex w-full items-center justify-between gap-3 rounded border p-3 text-left text-sm ${ALERT_STYLE[a.kind] || ""}`}
                onClick={() => apply({ ...EMPTY, ...(a.ip ? { ip: a.ip } : { who: a.email || "" }) })}>
                <span>{a.text}</span><span className="shrink-0 text-xs text-slate-500">{a.last || a.at ? when(a.last || a.at) : ""}</span>
              </button>))}
          </CardContent></Card>)}

      <Card><CardContent className="space-y-3 pt-4">
        <div className="grid gap-2 md:grid-cols-6">
          <select className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm md:col-span-2" value={filters.event} onChange={(e) => setFilters({ ...filters, event: e.target.value })}>
            {EVENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <Input placeholder="Persona (nome o email)" value={filters.who} onChange={(e) => setFilters({ ...filters, who: e.target.value })} />
          <Input placeholder="Indirizzo IP" value={filters.ip} onChange={(e) => setFilters({ ...filters, ip: e.target.value })} />
          <Input type="date" aria-label="Dal" value={filters.since} onChange={(e) => setFilters({ ...filters, since: e.target.value })} />
          <Input type="date" aria-label="Al" value={filters.until} onChange={(e) => setFilters({ ...filters, until: e.target.value })} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={filters.new_ip} onChange={(e) => setFilters({ ...filters, new_ip: e.target.checked })} /> Solo accessi da un indirizzo nuovo</label>
          <Button size="sm" onClick={() => apply(filters)}>Applica</Button>
          <Button size="sm" variant="ghost" onClick={() => apply(EMPTY)}>Azzera</Button>
          <span className="ml-auto text-sm text-slate-600">{total} righe</span>
        </div>
      </CardContent></Card>

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="p-3">Data e ora</th><th className="p-3">Evento</th><th className="p-3">Persona</th><th className="p-3">Indirizzo IP</th><th className="p-3">Dispositivo</th><th className="p-3">Note</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">{busy ? "Carico…" : "Nessuna riga con questi filtri."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t align-top">
                <td className="whitespace-nowrap p-3">{when(r.at)}</td>
                <td className="p-3"><Badge variant="outline" className={BADGE[r.event] || ""}>{r.label}</Badge></td>
                <td className="p-3"><span className="font-medium">{r.name || "—"}</span><span className="block text-xs text-slate-500">{r.email}{r.role ? ` · ${r.role}` : ""}</span></td>
                <td className="p-3"><button className="font-mono text-blue-800 underline-offset-2 hover:underline" onClick={() => apply({ ...EMPTY, ip: r.ip })}>{r.ip}</button></td>
                <td className="p-3 text-slate-700">{r.browser} · {r.os}<span className="block text-xs text-slate-500">{r.device}</span></td>
                <td className="p-3">{r.new_ip && <Badge variant="outline" className="mr-1 bg-amber-100 text-amber-800">IP nuovo</Badge>}<span className="text-xs text-slate-600">{r.detail}</span></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {rows.length < total && <div className="text-center"><Button variant="outline" disabled={busy} onClick={() => load(applied, rows.length)}>Carica altre righe</Button></div>}

      {summary && summary.top_ips.length > 0 && (
        <Card><CardHeader><CardTitle className="text-base">Indirizzi più attivi (ultimi 7 giorni)</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {summary.top_ips.map((t) => <button key={t.ip} className="rounded border px-3 py-1 font-mono text-sm hover:bg-slate-50" onClick={() => apply({ ...EMPTY, ip: t.ip })}>{t.ip} · {t.events}</button>)}
          </CardContent></Card>)}
    </div>
  );
}
